import React, { useEffect, useRef, useState } from 'react';
import { BaseLineDown } from '@/app/components/icons/BaseLineDown';
import { TagBlock } from '@/app/components/shared/ElementDialog/components/Tag';
import type { TagType } from '@/app/types';
import { cn } from '@/app/utils';

interface LabelFilter {
  tags: TagType[];
  tagSelections: string[];
  handleTagSelectorChange: (id: string) => void;
  className?: string;
  onDelete: (id: string) => void;
}

export const LabelFilter = React.forwardRef<HTMLDivElement, LabelFilter>(
  ({ className, tags, tagSelections, handleTagSelectorChange, onDelete }, ref) => {
    const tagsContainerRef = useRef<HTMLDivElement>(null);
    const [tagsOverflow, setTagsOverflow] = useState(false);
    const [isTagsExpanded, setIsTagsExpanded] = useState(false);

    const checkTagsOverflow = () => {
      const container = tagsContainerRef.current;
      if (!container) return;

      // 检查是否有子元素位于多行
      const children = Array.from(container.children) as HTMLElement[];
      if (children.length <= 1) return;

      // 获取第一个元素的底部位置作为基准行
      const firstRowBottom = children[0].getBoundingClientRect().bottom;

      // 检查是否有任何元素的顶部位置超过了第一行的底部位置
      const hasMultipleRows = children.some(
        (child) => child.getBoundingClientRect().top > firstRowBottom + 2, // 添加小容差
      );

      setTagsOverflow(hasMultipleRows);
    };

    // 检测标签是否超过一行
    useEffect(() => {
      checkTagsOverflow();
      window.addEventListener('resize', checkTagsOverflow);
      return () => window.removeEventListener('resize', checkTagsOverflow);
    }, [tagsContainerRef, tags]);

    return (
      <div ref={ref} className={cn(className)}>
        <div
          ref={tagsContainerRef}
          className={cn(
            'relative flex flex-wrap items-center gap-2 overflow-hidden pt-2 pr-6',
            isTagsExpanded ? 'max-h-full' : 'max-h-9',
          )}
        >
          {tags?.map((tag) => {
            const isSelected = tagSelections?.includes(tag.id || '') ?? false;
            return (
              <TagBlock
                tag={tag}
                key={tag.id}
                isSelected={isSelected}
                onTagSelect={() => handleTagSelectorChange(tag.id || '')}
                onDelete={onDelete}
                enableDelete={true}
                deleteClassName="h-4 w-4"
                innerClassName="text-xs"
                size="small"
                bgConfig={{
                  unSelect: 'bg-system-white10',
                  hover: 'hover:bg-system-white24',
                }}
              />
            );
          })}
          {tagsOverflow && (
            <div
              onClick={() => setIsTagsExpanded(!isTagsExpanded)}
              className={cn('rounded-full', !isTagsExpanded && 'absolute top-4 right-0')}
            >
              <BaseLineDown className={cn('transition-transform', isTagsExpanded && 'rotate-180')} />
            </div>
          )}
        </div>
      </div>
    );
  },
);
